import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Select, Popconfirm, Tag, Space, message } from 'antd';
import {
  UploadOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import {
  fetchRAGDocuments,
  uploadRAGDocument,
  deleteRAGDocument,
  updateRAGDocumentType,
} from '../api/restapi';
import '../assets/AllTabsCommon.css';

const DOC_TYPE_OPTIONS = [
  { value: 'external', label: 'External' },
  { value: 'internal', label: 'Internal' },
];

function RAGDocumentManagement({ userInfo, role }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Upload modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDocType, setUploadDocType] = useState('external');
  const [uploading, setUploading] = useState(false);

  // Inline type edit
  const [editTypeId, setEditTypeId] = useState(null);
  const [editTypeValue, setEditTypeValue] = useState('');

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const data = await fetchRAGDocuments();
      if (data.errorMessage) {
        message.error(data.errorMessage);
      } else {
        setDocuments(data.documents || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const handleUpload = async () => {
    if (!uploadFile) {
      message.error('Please select a file');
      return;
    }
    setUploading(true);
    try {
      const result = await uploadRAGDocument(uploadFile, uploadDocType);
      if (result.id) {
        message.success(`Uploaded "${result.filename}" — ${result.chunks_created} chunks created`);
        setUploadModalOpen(false);
        setUploadFile(null);
        setUploadDocType('external');
        loadDocuments();
      } else {
        message.error(result.detail || 'Upload failed');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId, filename) => {
    const result = await deleteRAGDocument(docId);
    if (result.errorMessage) {
      message.error(result.errorMessage);
    } else {
      message.success(`"${filename}" deleted`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
    }
  };

  const handleSaveType = async (docId) => {
    const result = await updateRAGDocumentType(docId, editTypeValue);
    if (result.errorMessage) {
      message.error(result.errorMessage);
    } else {
      message.success('Document type updated');
      setDocuments(prev =>
        prev.map(d => d.id === docId ? { ...d, document_type: editTypeValue } : d)
      );
      setEditTypeId(null);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const columns = [
    {
      title: 'Filename',
      dataIndex: 'filename',
      key: 'filename',
      ellipsis: true,
    },
    {
      title: 'Type',
      dataIndex: 'document_type',
      key: 'document_type',
      width: 200,
      render: (type, record) => {
        if (editTypeId === record.id) {
          return (
            <Space>
              <Select
                value={editTypeValue}
                onChange={setEditTypeValue}
                size="small"
                style={{ width: 110 }}
                options={DOC_TYPE_OPTIONS}
              />
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => handleSaveType(record.id)}
              />
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={() => setEditTypeId(null)}
              />
            </Space>
          );
        }
        return (
          <Space>
            <Tag color={type === 'internal' ? 'blue' : 'green'}>{type}</Tag>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => { setEditTypeId(record.id); setEditTypeValue(type); }}
            />
          </Space>
        );
      },
    },
    {
      title: 'Size',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 90,
      render: formatSize,
    },
    {
      title: 'Uploaded At',
      dataIndex: 'uploaded_at',
      key: 'uploaded_at',
      width: 170,
      render: (ts) => ts ? new Date(ts).toLocaleString() : '-',
    },
    {
      title: 'File Path',
      dataIndex: 'file_path',
      key: 'file_path',
      ellipsis: true,
      render: (path) => path ? (
        <span
          title={`${path}\n(click to copy)`}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            navigator.clipboard.writeText(path);
            message.success('Path copied to clipboard');
          }}
        >
          {path}
        </span>
      ) : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title="Delete this document?"
          description="This will remove the document and all its embeddings."
          onConfirm={() => handleDelete(record.id, record.filename)}
          okText="Delete"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="content-tab-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={() => setUploadModalOpen(true)}
        >
          Upload Document
        </Button>
        <Button icon={<ReloadOutlined />} onClick={loadDocuments} loading={loading}>
          Refresh
        </Button>
      </div>

      <Table
        dataSource={documents}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="middle"
        locale={{ emptyText: 'No documents uploaded yet' }}
      />

      <Modal
        title="Upload Document"
        open={uploadModalOpen}
        onCancel={() => { setUploadModalOpen(false); setUploadFile(null); setUploadDocType('external'); }}
        onOk={handleUpload}
        okText="Upload"
        confirmLoading={uploading}
        okButtonProps={{ disabled: !uploadFile }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
          <div>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>
              File <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            <input
              type="file"
              accept=".pdf,.txt,.md"
              onChange={(e) => setUploadFile(e.target.files[0] || null)}
            />
            {uploadFile && (
              <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                {uploadFile.name} ({formatSize(uploadFile.size)})
              </div>
            )}
          </div>
          <div>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>Document Type</div>
            <Select
              value={uploadDocType}
              onChange={setUploadDocType}
              style={{ width: '100%' }}
              options={DOC_TYPE_OPTIONS}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default RAGDocumentManagement;
